/**
 * The stamp on the letter being written, and the stamp book you change it from
 * (docs/POSTAGE_PLAN.md §3.2).
 *
 * - `ComposeStamp` sits in the corner of the compose card like the stamp on an
 *   envelope. The default class (the member's choice, else 1st once earned,
 *   else 2nd) is already stuck on, so sealing never needs a detour. On
 *   "Seal & send" the postmark franks it.
 * - `DockedStampBook` is the correspondent's page of the stamp book, docked
 *   beside the postal record wherever you reply to a letter: a 2nd-class pad
 *   that never runs out, the 1st-class pass if earned, the Express stamps in
 *   their slots, stamps for anyone, and this pair's stamp history. Tapping a
 *   stamp peels it out and sticks it on the reply.
 * - `StampPicker` is the same page as an overlay, for a brand-new letter where
 *   there is no postal record to dock beside.
 *
 * Express stays opt-in: nothing here ever picks it on its own, and the stamp is
 * only spent when the letter is sealed, server-side.
 *
 * Custom stamps (toiyp Plus): below the classes, the designs this member can
 * print their stamp with. A design is artwork only — the class (and so the
 * speed) is whatever was picked above, shown as a mark on the design's corner.
 * With postage switched off, the page holds just the designs.
 */

import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, X } from 'lucide-react';
import { PLUS_LOCKED_NOTE, POSTAGE_LABELS, type PostageClass } from '@toiyp/shared';
import { useAppSettings } from '../settings/api';
import { useStampBookQuery, type StampBookView, type StampCollection } from '../letters/api';
import { arrivesPhrase } from '../letters/eta';
import { FrankedStamp, PostageStamp } from './Stamp';
import { etaShort } from './format';
import { nextRefill, spentSinceLastLook } from './bookMemory';
import { historyLine } from './StampBook';
import { GiftBookLink } from './GiftStampBook';
import { EnvelopeThumb } from '../stationery/Envelope';
import type { PostageChoice } from './Postage';

/* ── The stamp in the envelope corner ───────────────────────────────────── */

export function ComposeStamp({
  choice,
  peerName,
  sealing = false,
  onOpen,
}: {
  choice: PostageChoice;
  peerName: string;
  /** The seal moment: the postmark lands on the stamp. */
  sealing?: boolean;
  /** Where the book already sits beside the letter, point there instead of opening an overlay. */
  onOpen?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const classed = choice.enabled && choice.selected && choice.selectedView;
  if (!classed && !choice.hasDesigns && !choice.hasPapers) return null;
  // With postage off, every letter goes 2nd class; a design still prints on it.
  const cls = choice.selected ?? 'second';
  const deliverAt = choice.selectedView?.deliverAt ?? null;
  const eta = deliverAt ? etaShort(deliverAt) : null;
  const design = choice.design;

  return (
    <>
      <button
        type="button"
        // Re-keyed on the class and design, so a new stamp plays its "stick" each time.
        key={`${cls}-${design?.id ?? 'plain'}`}
        className={`compose-stamp compose-stamp--${cls}${design ? ' compose-stamp--design' : ''}${sealing ? ' compose-stamp--sealing' : ''}`}
        onClick={() => (onOpen ? onOpen() : setOpen(true))}
        disabled={sealing}
        aria-haspopup={onOpen ? undefined : 'dialog'}
        aria-label={`Stamp: ${POSTAGE_LABELS[cls]}${design ? `, ${design.name}` : ''}${
          deliverAt ? `, arrives ${arrivesPhrase(deliverAt)}` : ''
        }. Change the stamp`}
        title={`${POSTAGE_LABELS[cls]}${design ? ` · ${design.name}` : ''}${eta ? ` · ${eta}` : ''} — tap to change`}
      >
        <span className="compose-stamp__art">
          {sealing ? (
            <FrankedStamp cls={cls} date={new Date()} size={design ? 'sheet' : 'medium'} design={design} />
          ) : (
            <PostageStamp cls={cls} size={design ? 'sheet' : 'medium'} design={design} />
          )}
        </span>
        <span className="compose-stamp__caption">
          {POSTAGE_LABELS[cls]}
          {eta ? ` · ${eta}` : ''}
        </span>
      </button>
      {open && !onOpen && <StampPicker choice={choice} peerName={peerName} onClose={() => setOpen(false)} />}
    </>
  );
}

/* ── One correspondent's page ───────────────────────────────────────────── */

type ClassView = NonNullable<PostageChoice['view']>['classes'][number];

function reducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/** "1st class pass · 3 Express" — the folded page, in a line. */
export function dockSummary(firstPass: boolean, express: number, forAnyone: number): string {
  const parts = [firstPass ? '1st class pass' : '2nd class'];
  if (express > 0) parts.push(`${express} Express`);
  if (forAnyone > 0) parts.push(`${forAnyone} for anyone`);
  return parts.join(' · ');
}

/**
 * One correspondent's page: a flat, even grid of identical slots in a fixed
 * order — 2nd class, 1st class, Express, then gifts. Held stamps are the stamp
 * art; anything not held is a dashed placeholder the size of a stamp with the
 * class name inside, so 1st class waits as a placeholder until earned and
 * Express shows its empty slots up to the cap. One plain caption per group.
 */
function BookPage({
  choice,
  onPicked,
  anyLetter = 0,
  arriving = 0,
}: {
  choice: PostageChoice;
  /** After the pick: close an overlay, or nothing when docked. */
  onPicked: () => void;
  /** Stamps not tied to anyone (gifted, later bought) — counted inside the Express balance. */
  anyLetter?: number;
  /** How many of this page's Express stamps arrived since it was last seen. */
  arriving?: number;
}) {
  const { expressMaxBalance } = useAppSettings();
  const [picking, setPicking] = useState<string | null>(null);
  const byClass = (cls: PostageClass): ClassView | undefined => choice.view?.classes.find((c) => c.class === cls);
  const second = byClass('second');
  const first = byClass('first');
  const express = byClass('express');
  const forAnyone = Math.min(anyLetter, express?.balance ?? 0);
  const held = Math.max(0, (express?.balance ?? 0) - forAnyone);

  // A stamp spent since the book was last open leaves a postmarked gap, once.
  const [gaps, setGaps] = useState(0);
  const looked = useRef(false);
  useEffect(() => {
    if (looked.current || !express) return;
    looked.current = true;
    setGaps(spentSinceLastLook(choice.relationshipId, held));
  }, [express, held, choice.relationshipId]);

  const pick = (cls: PostageClass, id: string) => {
    setPicking(id);
    window.setTimeout(
      () => {
        setPicking(null);
        choice.choose(cls);
        onPicked();
      },
      reducedMotion() ? 0 : 220,
    );
  };

  const selected = choice.selected;
  // Every row is the same width: the Express cap sets the columns (at least 4).
  const cols = Math.max(4, expressMaxBalance, held + gaps);
  const slots = cols;
  const fresh = Math.min(arriving, held);
  const expressUsable = express?.state === 'available';
  const firstEarned = first?.state === 'available';
  const refill = nextRefill().toLocaleDateString([], { day: 'numeric', month: 'long' });

  if (!choice.enabled || !choice.view) {
    return (
      <div className="stamp-grid" style={{ '--cols': cols } as React.CSSProperties}>
        <DesignShelf choice={choice} onPicked={onPicked} />
        <PaperShelf choice={choice} />
      </div>
    );
  }

  return (
    <div className="stamp-grid" style={{ '--cols': cols } as React.CSSProperties}>
      <div className="stamp-grid__group">
        <ol className="stamp-grid__slots">
          {second &&
            Array.from({ length: cols }, (_, i) => (
              <StampSlot
                key={`second-${i}`}
                id={`second-${i}`}
                cls="second"
                selected={selected === 'second' && i === 0}
                picking={picking === `second-${i}`}
                onPick={() => pick('second', `second-${i}`)}
                ariaLabel={`2nd class, always free — arrives ${arrivesPhrase(second.deliverAt)}`}
              />
            ))}
        </ol>
        <p className="stamp-grid__caption">2nd class · free · {second ? etaShort(second.deliverAt) : ''}</p>
      </div>

      <div className="stamp-grid__group">
        <ol className="stamp-grid__slots">
          {Array.from({ length: cols }, (_, i) =>
            firstEarned ? (
              <StampSlot
                key={`first-${i}`}
                id={`first-${i}`}
                cls="first"
                selected={selected === 'first' && i === 0}
                picking={picking === `first-${i}`}
                onPick={() => pick('first', `first-${i}`)}
                ariaLabel={`1st class pass — arrives ${arrivesPhrase(first!.deliverAt)}`}
              />
            ) : (
              <Placeholder key={`first-${i}`} label="1st" />
            ),
          )}
        </ol>
        <p className="stamp-grid__caption">
          {firstEarned ? `1st class pass · ${etaShort(first!.deliverAt)}` : `1st class · ${first?.hint ?? 'Comes with writing regularly.'}`}
        </p>
      </div>

      <div className="stamp-grid__group">
        <ol className="stamp-grid__slots" aria-label="Express stamps">
          {Array.from({ length: slots }, (_, i) => {
            if (i < held) {
              const id = `express-${i}`;
              const isNew = i >= held - fresh;
              return (
                <StampSlot
                  key={id}
                  id={id}
                  cls="express"
                  selected={selected === 'express' && i === held - 1}
                  picking={picking === id}
                  disabled={!expressUsable}
                  arriving={isNew ? (i - (held - fresh)) * 160 + 200 : undefined}
                  onPick={() => pick('express', id)}
                  ariaLabel={
                    expressUsable ? `Use an Express stamp — arrives ${arrivesPhrase(express!.deliverAt)}` : 'Express stamp, paused'
                  }
                />
              );
            }
            if (i < held + gaps) {
              // Spent since last time: franked, then fading to an empty slot.
              return (
                <li key={`gap-${i}`} className="stamp-grid__slot stamp-grid__slot--spent" aria-hidden="true">
                  <FrankedStamp cls="express" date={new Date()} size="medium" />
                </li>
              );
            }
            return <Placeholder key={`empty-${i}`} label="Express" />;
          })}
        </ol>
        <p className="stamp-grid__caption">
          {!express
            ? 'Express · Comes with writing regularly.'
            : express.state === 'paused' || express.state === 'locked'
              ? `Express · ${express.hint}`
              : held > 0
                ? `Express · ${etaShort(express.deliverAt)} · more on ${refill}`
                : `Express · more on ${refill}`}
        </p>
      </div>

      {forAnyone > 0 && express && (
        <div className="stamp-grid__group">
          <ol className="stamp-grid__slots" aria-label="Gifted stamps">
            {Array.from({ length: Math.max(cols, forAnyone) }, (_, i) => {
              if (i >= forAnyone) return <Placeholder key={`gift-${i}`} label="Gift" />;
              const id = `anyone-${i}`;
              return (
                <StampSlot
                  key={id}
                  id={id}
                  cls="express"
                  selected={false}
                  picking={picking === id}
                  disabled={!expressUsable}
                  onPick={() => pick('express', id)}
                  ariaLabel={expressUsable ? 'Use a gifted Express stamp' : 'Gifted Express stamp — Express is not on with this pair'}
                />
              );
            })}
          </ol>
          <p className="stamp-grid__caption">
            {expressUsable ? 'Gifted · for anyone' : 'Gifted · works where Express is on'}
          </p>
        </div>
      )}

      <DesignShelf choice={choice} onPicked={onPicked} />
      <PaperShelf choice={choice} />
    </div>
  );
}

/** Who a collection came from, in a few words — never a price. */
export function collectionCaption(c: StampCollection): string {
  switch (c.access) {
    case 'plus':
      return 'toiyp Plus';
    case 'gift': {
      const g = c.giftedBy;
      const who = g?.handle ? `@${g.handle}` : g?.displayName;
      return who ? `a gift from ${who}` : 'a gift';
    }
    case 'admin':
      return 'from toiyp';
    case 'free':
      return 'for everyone';
    case 'locked':
      return PLUS_LOCKED_NOTE;
  }
}

/**
 * The designs this member can print their stamp with, one even row per
 * collection, the class mark on each. Tapping the chosen one again (or "plain")
 * goes back to the class stamp. Locked collections are not offered here — they
 * wait in the stamp book as previews — just one quiet line says they exist.
 */
function DesignShelf({ choice, onPicked }: { choice: PostageChoice; onPicked: () => void }) {
  // Books that hold stamp designs (a book may hold only paper).
  const collections = (choice.stamps?.collections ?? []).filter((c) => c.designs.length > 0);
  const usable = collections.filter((c) => c.access !== 'locked');
  const locked = collections.length - usable.length;
  if (usable.length === 0 && locked === 0) return null;
  const cls = choice.selected ?? 'second';
  const selectedId = choice.design?.id ?? null;
  const pick = (id: string | null) => {
    choice.chooseDesign(id);
    onPicked();
  };

  return (
    <div className="stamp-designs">
      <p className="stamp-designs__title">Stamp designs</p>
      {usable.map((c) => (
        <div className="stamp-grid__group" key={c.id}>
          <ol className="stamp-grid__slots stamp-grid__slots--designs" aria-label={`${c.name} designs`}>
            {c.designs.map((d) => {
              const on = selectedId === d.id;
              return (
                <li key={d.id} className={`stamp-grid__slot stamp-grid__slot--design${on ? ' is-selected' : ''}`}>
                  <button
                    type="button"
                    className="stamp-grid__stamp"
                    aria-pressed={on}
                    aria-label={on ? `${d.name} — on this letter. Tap for the plain stamp` : `Print the stamp with ${d.name}`}
                    title={d.name}
                    onClick={() => pick(on ? null : d.id)}
                  >
                    <PostageStamp cls={cls} size="envelope" design={d} />
                  </button>
                </li>
              );
            })}
          </ol>
          <p className="stamp-grid__caption">
            {c.name} · {collectionCaption(c)}
          </p>
        </div>
      ))}
      {selectedId && (
        <button type="button" className="linkish stamp-designs__plain" onClick={() => pick(null)}>
          Use the plain {POSTAGE_LABELS[cls]} stamp
        </button>
      )}
      {usable.length === 0 && locked > 0 && (
        <p className="stamp-grid__caption">
          Designed stamps are {PLUS_LOCKED_NOTE.charAt(0).toLowerCase() + PLUS_LOCKED_NOTE.slice(1)}. You can see them in your stamp book.
        </p>
      )}
      <p className="stamp-designs__note">Designs change how the stamp looks, never how fast it goes.</p>
      <GiftBookLink relationshipId={choice.relationshipId} label="Gift them a stamp book" />
    </div>
  );
}

/** A held stamp in its slot — tap to use. */
function StampSlot({
  id,
  cls,
  selected,
  picking,
  disabled = false,
  arriving,
  onPick,
  ariaLabel,
}: {
  id: string;
  cls: PostageClass;
  selected: boolean;
  picking: boolean;
  disabled?: boolean;
  /** Animation delay (ms) when this stamp has just arrived. */
  arriving?: number;
  onPick: () => void;
  ariaLabel: string;
}) {
  return (
    <li
      data-id={id}
      className={`stamp-grid__slot${selected ? ' is-selected' : ''}${picking ? ' is-picking' : ''}${
        arriving != null ? ' stamp-slot--new' : ''
      }`}
      style={arriving != null ? ({ '--arrive-delay': `${arriving}ms` } as React.CSSProperties) : undefined}
    >
      <button
        type="button"
        className="stamp-grid__stamp"
        aria-pressed={selected}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={onPick}
      >
        <PostageStamp cls={cls} size="sheet" />
      </button>
    </li>
  );
}

/** A stamp not held: a dashed outline the size of a stamp, the class inside. */
function Placeholder({ label }: { label: string }) {
  return (
    <li className="stamp-grid__slot stamp-grid__slot--empty" aria-hidden="true">
      <span>{label}</span>
    </li>
  );
}

/* ── Docked beside the postal record ─────────────────────────────────────── */

const COLLAPSE_KEY = 'toiyp:stamp-dock-collapsed';
const SEEN_KEY = 'toiyp:stamp-dock-seen';

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
}

/** When this pair's page was last seen; stamps that arrived after it drop in. */
function lastSeen(relationshipId: string): number {
  try {
    const map = JSON.parse(localStorage.getItem(SEEN_KEY) ?? '{}') as Record<string, string>;
    return map[relationshipId] ? new Date(map[relationshipId]!).getTime() : 0;
  } catch {
    return 0;
  }
}

function markSeen(relationshipId: string): void {
  try {
    const map = JSON.parse(localStorage.getItem(SEEN_KEY) ?? '{}') as Record<string, string>;
    map[relationshipId] = new Date().toISOString();
    localStorage.setItem(SEEN_KEY, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

/** Express stamps for this pair added since `seenAt`, from the ledger history. */
export function arrivedSince(history: StampBookView['history'], relationshipId: string, seenAt: number): number {
  return history
    .filter((h) => h.kind === 'allowance' && h.relationshipId === relationshipId && new Date(h.at).getTime() > seenAt)
    .reduce((n, h) => n + h.delta, 0);
}

export const DockedStampBook = forwardRef<
  HTMLElement,
  { choice: PostageChoice; peerName: string; onPicked: () => void; highlight?: boolean }
>(function DockedStampBook({ choice, peerName, onPicked, highlight = false }, ref) {
  const { data: book } = useStampBookQuery(undefined, { skip: !choice.enabled });
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [seenAt] = useState(() => lastSeen(choice.relationshipId));
  const page = book?.pages.find((p) => p.relationshipId === choice.relationshipId);
  // Stamps for anyone only show (and count) where Express is part of the page.
  const expressOnPage = choice.view?.classes.some((c) => c.class === 'express') ?? false;
  const anyLetter = expressOnPage ? (book?.anyLetter.express ?? 0) : 0;
  const arriving = book ? arrivedSince(book.history, choice.relationshipId, seenAt) : 0;
  const history = useMemo(
    () => (book?.history ?? []).filter((h) => h.relationshipId === choice.relationshipId || h.relationshipId == null),
    [book, choice.relationshipId],
  );

  useEffect(() => {
    if (book) markSeen(choice.relationshipId);
  }, [book, choice.relationshipId]);

  if (!(choice.enabled && choice.view) && !choice.hasDesigns) return null;
  const who = peerName.startsWith('@') ? peerName : `@${peerName}`;
  const firstPass = (choice.view?.classes ?? []).some((c) => c.class === 'first' && c.state === 'available');
  const expressHeld = Math.max(0, (choice.view?.expressBalance ?? 0) - anyLetter);
  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    try {
      localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0');
    } catch {
      /* ignore */
    }
  };

  return (
    <section
      ref={ref}
      className={`stamp-dock${collapsed ? ' is-collapsed' : ''}${highlight ? ' is-highlighted' : ''}`}
      aria-label={`Your stamps for ${who}`}
    >
      {/* On a phone the page folds to one line behind a flat chevron. */}
      <button
        type="button"
        className="stamp-dock__fold"
        onClick={toggle}
        aria-expanded={!collapsed}
        aria-label={collapsed ? 'Show your stamps' : 'Fold your stamps away'}
      >
        <ChevronDown size={16} aria-hidden="true" />
      </button>
      <button type="button" className="stamp-dock__folded" onClick={toggle} aria-expanded={false}>
        <span className="stamp-dock__strip" aria-hidden="true">
          {firstPass && <PostageStamp cls="first" size="mini" />}
          {Array.from({ length: Math.min(expressHeld, 4) }, (_, i) => (
            <PostageStamp key={i} cls="express" size="mini" />
          ))}
          {!firstPass && expressHeld === 0 && <PostageStamp cls="second" size="mini" />}
          {choice.design && <PostageStamp cls={choice.selected ?? 'second'} size="mini" design={choice.design} />}
        </span>
        <span className="stamp-dock__summary">
          {dockSummary(firstPass, expressHeld, anyLetter)}
          {choice.design ? ` · ${choice.design.name}` : ''}
        </span>
      </button>
      <div className="stamp-dock__page">
        <BookPage
          choice={choice}
          onPicked={onPicked}
          anyLetter={anyLetter}
          arriving={page ? arriving : 0}
        />
        {history.length > 0 && (
          <details className="stamp-dock__history">
            <summary>history</summary>
            <ul>
              {history.slice(0, 8).map((h, i) => (
                <li key={`${h.at}-${i}`}>
                  <span>{historyLine(h, who)}</span>
                  <time dateTime={h.at}>{new Date(h.at).toLocaleDateString([], { day: 'numeric', month: 'short' })}</time>
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </section>
  );
});

/* ── The overlay, for a brand-new letter ────────────────────────────────── */

export function StampPicker({
  choice,
  peerName,
  onClose,
}: {
  choice: PostageChoice;
  peerName: string;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    ref.current?.querySelector<HTMLElement>('[aria-pressed="true"]')?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const who = peerName.startsWith('@') ? peerName : `@${peerName}`;

  return createPortal(
    <div className="sheet-backdrop" onClick={onClose}>
      <div
        className="sheet stamp-picker"
        role="dialog"
        aria-modal="true"
        aria-label={`Choose a stamp for your letter to ${who}`}
        ref={ref}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sheet__grip" aria-hidden="true" />
        <button type="button" className="stamp-picker__close" onClick={onClose} aria-label="Close your stamps">
          <X size={16} />
        </button>
        <div className="stamp-picker__body">
          <BookPage choice={choice} onPicked={onClose} />
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Letter paper (stationery): plain paper first (the default), then the papers
 * this member can write on, then locked previews, dimmed, with one quiet line.
 * The choice is remembered for this correspondent. Paper never changes the
 * class or the timing.
 */
function PaperShelf({ choice }: { choice: PostageChoice }) {
  const collections = (choice.stamps?.collections ?? []).filter((c) => c.papers.length > 0);
  const hasEnvelopes = (choice.stamps?.collections ?? []).some((c) => c.envelopes.length > 0);
  if (collections.length === 0 && !hasEnvelopes) return null;
  const selectedId = choice.paper?.id ?? null;
  const usable = collections.filter((c) => c.access !== 'locked');
  const locked = collections.filter((c) => c.access === 'locked');

  return (
    <div className="paper-shelf">
      <p className="stamp-designs__title">Paper</p>
      <ol className="paper-shelf__slots" aria-label="Letter paper">
        <li>
          <button
            type="button"
            className={`paper-shelf__paper paper-shelf__paper--plain${selectedId ? '' : ' is-selected'}`}
            aria-pressed={!selectedId}
            onClick={() => choice.choosePaper(null)}
          >
            <span className="paper-shelf__thumb" aria-hidden="true" />
            <span className="paper-shelf__name">Plain</span>
          </button>
        </li>
        {usable.flatMap((c) =>
          c.papers.map((p) => {
            const on = selectedId === p.id;
            return (
              <li key={p.id}>
                <button
                  type="button"
                  className={`paper-shelf__paper${on ? ' is-selected' : ''}`}
                  aria-pressed={on}
                  aria-label={`Write on ${p.name}, from ${c.name}`}
                  onClick={() => choice.choosePaper(on ? null : p.id)}
                >
                  <span className="paper-shelf__thumb" style={{ backgroundImage: `url(${p.imageUrl})` }} aria-hidden="true" />
                  <span className="paper-shelf__name">{p.name}</span>
                </button>
              </li>
            );
          }),
        )}
        {locked.flatMap((c) =>
          c.papers.map((p) => (
            <li key={p.id} className="paper-shelf__locked" title={PLUS_LOCKED_NOTE}>
              <span className="paper-shelf__paper" aria-label={`${p.name}, ${PLUS_LOCKED_NOTE}`}>
                <span className="paper-shelf__thumb" style={{ backgroundImage: `url(${p.imageUrl})` }} aria-hidden="true" />
                <span className="paper-shelf__name">{p.name}</span>
              </span>
            </li>
          )),
        )}
      </ol>
      {locked.length > 0 && <p className="stamp-grid__caption">{locked.map((c) => c.name).join(', ')} · {PLUS_LOCKED_NOTE}</p>}
      <EnvelopeRow choice={choice} />
      <p className="stamp-designs__note">Paper and envelopes change how the letter looks, never how fast it goes.</p>
    </div>
  );
}

/**
 * Envelopes: plain first, then this member's envelopes, then locked previews.
 * With nothing chosen for this correspondent, the envelope follows the paper's
 * own book (a set travels together); choosing — plain included — is remembered.
 */
function EnvelopeRow({ choice }: { choice: PostageChoice }) {
  const books = (choice.stamps?.collections ?? []).filter((c) => c.envelopes.length > 0);
  if (books.length === 0) return null;
  const usable = books.filter((c) => c.access !== 'locked');
  const locked = books.filter((c) => c.access === 'locked');
  const selectedId = choice.envelope?.id ?? null;
  return (
    <>
      <p className="stamp-designs__title">Envelope</p>
      <ol className="paper-shelf__slots" aria-label="Envelopes">
        <li>
          <button
            type="button"
            className={`paper-shelf__paper${selectedId ? '' : ' is-selected'}`}
            aria-pressed={!selectedId}
            onClick={() => choice.chooseEnvelope(null)}
          >
            <span className="paper-shelf__env paper-shelf__env--plain" aria-hidden="true" />
            <span className="paper-shelf__name">Plain</span>
          </button>
        </li>
        {usable.flatMap((c) =>
          c.envelopes.map((e) => {
            const on = selectedId === e.id;
            return (
              <li key={e.id}>
                <button
                  type="button"
                  className={`paper-shelf__paper${on ? ' is-selected' : ''}`}
                  aria-pressed={on}
                  aria-label={`Send in the ${e.name} envelope, from ${c.name}`}
                  onClick={() => choice.chooseEnvelope(e.id)}
                >
                  <EnvelopeThumb envelope={e} title={e.name} />
                  <span className="paper-shelf__name">{e.name}</span>
                </button>
              </li>
            );
          }),
        )}
        {locked.flatMap((c) =>
          c.envelopes.map((e) => (
            <li key={e.id} className="paper-shelf__locked" title={PLUS_LOCKED_NOTE}>
              <span className="paper-shelf__paper" aria-label={`${e.name}, ${PLUS_LOCKED_NOTE}`}>
                <EnvelopeThumb envelope={e} title={`${e.name} · ${PLUS_LOCKED_NOTE}`} />
                <span className="paper-shelf__name">{e.name}</span>
              </span>
            </li>
          )),
        )}
      </ol>
      {choice.envelopeMatchesPaper && <p className="stamp-grid__caption">Matches your paper</p>}
    </>
  );
}
